import {
  Button,
  ChatSearchIcon,
  ConversationCandidateResponse,
  Disclaimer,
  Div,
  lightTheme,
  ModalPortal,
  Radio,
  renderRole,
  SearchBar,
  SearchField,
} from '@vgrubs/components';
import { ReactNode, useEffect, useState } from 'react';
import { ConversationPersonRow } from './components/ConversationPersonRow';
import { CONVERSATION_MODAL_HEIGHT } from './conversationModal';
import { CandidateList } from './NewConversationModal.styled';

/**
 * Picking people to add to a group - colleagues, or for a merchant group also
 * restaurants and sales agents.
 *
 * A modal of its own, opened from the member list rather than unfolding inside
 * it. Inline, the search results pushed the members it was adding to off the
 * bottom of the panel - so you lost sight of who was already there at exactly
 * the moment you were deciding who else to add - and the details modal ended up
 * with two scroll containers stacked inside one another.
 *
 * It stacks ABOVE the details modal it was opened from (see `zIndex`), and
 * shares that modal's pinned height so the two do not jump in size as one
 * replaces the other on screen.
 */
export const AddMembersModal = ({
  isOpen,
  onClose,
  query,
  onQueryChange,
  candidates,
  onAdd,
  busy,
  filter,
}: {
  isOpen: boolean;
  onClose: () => void;
  query: string;
  onQueryChange: (query: string) => void;
  /** Already filtered down to people not in the group. */
  candidates: ConversationCandidateResponse[];
  /** Everyone picked, in one call - the API takes a list of ids. */
  onAdd: (picked: ConversationCandidateResponse[]) => void;
  busy?: boolean;
  /** Under the search - a merchant group's location filter. */
  filter?: ReactNode;
}) => {
  /**
   * The people picked so far, held as whole candidates rather than as ids.
   *
   * Searching again replaces the list under them, and an id alone would leave
   * the button counting people it could no longer name. Keeping the objects
   * means a pick made under one search survives the next one.
   */
  const [picked, setPicked] = useState<ConversationCandidateResponse[]>([]);

  useEffect(() => {
    if (!isOpen) setPicked([]);
  }, [isOpen]);

  const toggle = (candidate: ConversationCandidateResponse) =>
    setPicked((current) =>
      current.some((item) => item.userId === candidate.userId)
        ? current.filter((item) => item.userId !== candidate.userId)
        : [...current, candidate]
    );

  return (
    <ModalPortal
      isOpen={isOpen}
      closeModal={onClose}
      header="Add Members"
      width="520px"
      minHeight={CONVERSATION_MODAL_HEIGHT}
      maxHeight={CONVERSATION_MODAL_HEIGHT}
      /* Above the details modal underneath, whose default is 130. */
      zIndex={140}
      footer={
        <Button
          $w="100%"
          $wSM="100%"
          $selected
          disabled={busy || picked.length === 0}
          onClick={() => onAdd(picked)}
        >
          {picked.length > 0 ? `Add (${picked.length})` : 'Add'}
        </Button>
      }
      $numOfFloatingButtons={1}
    >
      <SearchBar>
        <SearchField
          value={query}
          placeholder="Search"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onQueryChange(event.target.value)
          }
        />
        <ChatSearchIcon />
      </SearchBar>

      {filter}

      <CandidateList>
        {candidates.map((candidate) => (
          <ConversationPersonRow
            key={candidate.userId}
            name={candidate.name}
            subtitle={[renderRole(candidate.role), candidate.phoneNumber]
              .filter(Boolean)
              .join(' · ')}
            /* Which restaurant a merchant is with - null for a colleague, so
               an internal group's rows stay one line. */
            detail={candidate.locations}
            selected={picked.some((item) => item.userId === candidate.userId)}
            trailing={
              <Radio
                checked={picked.some(
                  (item) => item.userId === candidate.userId
                )}
              />
            }
            onClick={() => toggle(candidate)}
          />
        ))}

        {candidates.length === 0 && (
          <Div $flex $center $p="24px 0">
            <Disclaimer $m="0" $center $color={lightTheme.lightGray}>
              {query
                ? 'Nobody matching is left to add.'
                : 'Everyone is already in this group.'}
            </Disclaimer>
          </Div>
        )}
      </CandidateList>
    </ModalPortal>
  );
};
